import { DEFAULT_PAGE_INFO } from '@apeiron/common'
import { OrbitalTrackFilterInput, OrbitalTrackSortBy } from '@apeiron/orbital'
import {
  deserializeListData,
  deserializePageInfo,
  PageInfo,
  PageInput,
  serializePagingInput
} from '@apeiron/temp'
import { useLazyQuery } from '@apollo/client'
import { publicStarRentalLiteSchema } from '@src/deserialize/yup/orbitalTrack'
import graphql from '@src/graphql/query/getPublicStarRental'
import { PublicStarRentalLite } from '@src/types/orbitalTrack'
import { useCallback, useState } from 'react'

const useGetPublicStarRentals = (): Hook => {
  const [query, { loading }] = useLazyQuery(graphql)

  const [data, setData] = useState<PublicStarRentalLite[]>([])

  const [pageInfo, setPageInfo] = useState<PageInfo>(DEFAULT_PAGE_INFO)

  const fetch = useCallback(
    async (request: Request): Promise<Response> => {
      const {
        filterInput = {},
        pageInput = {},
        sortInput = OrbitalTrackSortBy.StarOrbitalTrackSlotAsc
      } = request

      const response = await query({
        variables: {
          filterInput,
          pagingInput: serializePagingInput(pageInput),
          sortBy: sortInput
        }
      })

      const rentals = deserializeListData<PublicStarRentalLite>(
        response,
        publicStarRentalLiteSchema
      )

      const pageInfo = deserializePageInfo(response, pageInput)

      setData(rentals)
      setPageInfo(pageInfo)

      return {
        data: rentals,
        pageInfo
      }
    },
    [query]
  )

  return {
    data,
    loading,
    pageInfo,
    fetch
  }
}

type Request = {
  filterInput?: OrbitalTrackFilterInput
  pageInput?: PageInput
  sortInput?: OrbitalTrackSortBy
}

type Response = {
  data: PublicStarRentalLite[]
  pageInfo: PageInfo
}

type Hook = {
  data: PublicStarRentalLite[]
  loading: boolean
  pageInfo: PageInfo
  fetch: (request: Request) => Promise<Response>
}

export default useGetPublicStarRentals
